import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import { useToast } from '../../context/ToastContext';
import ConfirmModal from '../../components/ui/ConfirmModal';
import Modal from '../../components/ui/Modal';
import {
  TOPICS, MARKS, TOPIC_LABEL, TopicIcon, Icon, Rich, PostHeader, PostSections, TopicJump, formatDate,
} from '../../components/changelog/changelogUi';

/**
 * Admin Panel › Changelog — a bot frissítéseinek szerkesztője (docs/changelog.md).
 * Owner ÉS admin írhat. A látványterv: „MortisEngine Changelog” vászon,
 * Editor / EditorDiscord / Import rajzlap.
 *
 * A képet a BÖNGÉSZŐ készíti elő: 1920 px-nél szélesebbet kicsinyít, és WebP-be
 * teszi (ha az kisebb); a GIF marad, hogy az animáció megmaradjon. A szerver
 * csak ellenőriz és tárol.
 */

const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const MAX_WIDTH = 1920;

let keySeq = 0;
const nextKey = () => `k${++keySeq}`;

function emptyDraft() {
  return {
    id: null, status: 'draft', slug: '', version: '', title: '', summary: '', action: '',
    sections: [{ key: nextKey(), topic: 'autopilot', title: '', blocks: [{ key: nextKey(), type: 'item', mark: 'new', text: '' }] }],
  };
}

function fromApi(p) {
  return {
    id: p.id, status: p.status, slug: p.slug, version: p.version || '', title: p.title, summary: p.summary || '',
    action: p.action || '', publishedAt: p.publishedAt, updatedAt: p.updatedAt, updatedBy: p.updatedBy,
    discordMessages: p.discordMessages || 0,
    sections: p.sections.map(s => ({
      key: nextKey(), topic: s.topic, title: s.title || '',
      blocks: s.blocks.map(b => (b.type === 'image'
        ? { key: nextKey(), type: 'image', imageId: b.imageId, caption: b.caption || '', url: b.url, width: b.width, height: b.height }
        : { key: nextKey(), type: 'item', mark: b.mark, text: b.text })),
    })),
  };
}

/** A mentendő alak — a kulcsok és a megjelenítéshez kellő mezők nélkül. */
function toBody(d) {
  return {
    version: d.version.trim(), title: d.title.trim(), summary: d.summary.trim(), action: d.action.trim(),
    sections: d.sections.map(s => ({
      topic: s.topic, title: s.title.trim(),
      blocks: s.blocks
        .filter(b => !b.uploading && (b.type === 'image' || b.text.trim()))
        .map(b => (b.type === 'image' ? { type: 'image', imageId: b.imageId, caption: b.caption.trim() }
          : { type: 'item', mark: b.mark, text: b.text.trim() })),
    })),
  };
}

/** A szerkesztett állapot a nyilvános megjelenítő alakjában (előnézet). */
function toView(d) {
  const body = toBody(d);
  const byKey = new Map();
  d.sections.forEach(s => s.blocks.forEach(b => { if (b.type === 'image') byKey.set(b.imageId, b); }));
  return {
    slug: d.slug || 'preview', version: d.version.trim() || null, title: body.title || 'Untitled update',
    summary: body.summary, action: body.action || null, publishedAt: d.publishedAt || null,
    sections: body.sections.map(s => ({
      ...s,
      blocks: s.blocks.map(b => (b.type === 'image' ? { ...b, url: byKey.get(b.imageId)?.url, width: byKey.get(b.imageId)?.width, height: byKey.get(b.imageId)?.height } : b)),
    })),
  };
}

function errText(err, fallback) {
  return err?.response?.data?.error || fallback;
}

/** Kép előkészítése feltöltéshez (lásd a fájl fejét). */
async function prepareImage(file) {
  if (file.type === 'image/gif') return file;
  if (!/^image\/(png|jpeg|webp)$/.test(file.type)) throw new Error('Only PNG, JPG, WebP or GIF pictures can be used.');
  let bmp;
  try { bmp = await createImageBitmap(file); } catch { return file; }
  const scale = Math.min(1, MAX_WIDTH / bmp.width);
  const w = Math.max(1, Math.round(bmp.width * scale));
  const h = Math.max(1, Math.round(bmp.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').drawImage(bmp, 0, 0, w, h);
  bmp.close?.();
  const blob = await new Promise(res => canvas.toBlob(res, 'image/webp', 0.9));
  if (!blob || blob.type !== 'image/webp') return scale < 1 ? (blob || file) : file;
  // Kicsinyítés nélkül csak akkor cseréljük, ha a WebP tényleg kisebb.
  return (scale === 1 && blob.size >= file.size) ? file : blob;
}

function fmtBytes(n) {
  if (!n) return '';
  return n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
}

function fmtTime(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

// ════════════════════════════════════════════════════════════════════════════
export default function ChangelogAdmin() {
  const { toast } = useToast();
  const [list, setList] = useState(null);
  // Posztol-e ez a szerver a Discordra (a béta és a helyi gép nem — a lista mondja meg).
  const [discordEnabled, setDiscordEnabled] = useState(true);
  const [draft, setDraft] = useState(null);
  const [saved, setSaved] = useState('');
  const [view, setView] = useState('edit');
  const [busy, setBusy] = useState(false);
  const [opts, setOpts] = useState({ discord: true, ping: true });
  const [confirm, setConfirm] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const activeSection = useRef(0);
  const editorRef = useRef(null);

  const dirty = !!draft && JSON.stringify(toBody(draft)) !== saved;
  const uploading = !!draft && draft.sections.some(s => s.blocks.some(b => b.uploading));

  const loadList = useCallback(() => {
    api.get('/changelog/admin/posts')
      .then(res => { setList(res.data.posts); setDiscordEnabled(res.data.discordEnabled !== false); })
      .catch(err => toast.error(errText(err, 'The changelog list could not be loaded.')));
  }, [toast]);

  useEffect(() => { loadList(); }, [loadList]);

  const guard = (fn) => {
    if (!dirty) { fn(); return; }
    setConfirm({
      title: 'Discard the changes?', message: 'This update has unsaved changes. They are lost if you leave it now.',
      label: 'Discard', danger: true, onConfirm: () => { setConfirm(null); fn(); },
    });
  };

  const openPost = (id) => guard(() => {
    api.get(`/changelog/admin/posts/${id}`)
      .then(res => {
        const d = fromApi(res.data.post);
        setDraft(d);
        setSaved(JSON.stringify(toBody(d)));
        setView('edit');
      })
      .catch(err => toast.error(errText(err, 'The update could not be opened.')));
  });

  const newPost = () => guard(() => {
    const d = emptyDraft();
    setDraft(d);
    setSaved('');
    setView('edit');
    activeSection.current = 0;
  });

  const update = (fn) => setDraft(d => {
    const next = { ...d, sections: d.sections.map(s => ({ ...s, blocks: [...s.blocks] })) };
    fn(next);
    return next;
  });

  // ── mentés / közzététel ──
  const reportDiscord = (discord, done) => {
    if (!discord) { toast.success(done); return; }
    const n = discord.messages;
    switch (discord.state) {
      case 'posted': toast.success(`${done} Posted to Discord (${n} message${n === 1 ? '' : 's'}).`); break;
      case 'updated': toast.success(`${done} The Discord post was updated too.`); break;
      case 'removed': toast.success(`${done} The Discord post was removed.`); break;
      case 'off': toast.success(`${done} Discord is not connected on this server, nothing was posted.`); break;
      case 'failed': toast.error(`${done} But Discord failed: ${discord.error || 'the bot did not answer'}.`); break;
      default: toast.success(done);
    }
  };

  const save = async ({ quiet = false } = {}) => {
    if (uploading) { toast.error('Wait until the pictures are uploaded.'); return null; }
    if (!draft.title.trim()) { toast.error('Give the update a title.'); return null; }
    setBusy(true);
    try {
      const body = toBody(draft);
      const res = draft.id
        ? await api.put(`/changelog/admin/posts/${draft.id}`, body, { timeout: 40000 })
        : await api.post('/changelog/admin/posts', body);
      const d = fromApi(res.data.post);
      setDraft(d);
      setSaved(JSON.stringify(toBody(d)));
      loadList();
      if (!quiet) reportDiscord(res.data.discord, draft.status === 'published' ? 'Saved — the update is changed on the site.' : 'Draft saved.');
      return d;
    } catch (err) {
      toast.error(errText(err, 'The update could not be saved.'));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    let d = draft;
    if (!d.id || dirty) { d = await save({ quiet: true }); if (!d) return; }
    setBusy(true);
    try {
      const res = await api.post(`/changelog/admin/posts/${d.id}/publish`,
        discordEnabled ? opts : { discord: false }, { timeout: 40000 });
      const nd = fromApi(res.data.post);
      setDraft(nd);
      setSaved(JSON.stringify(toBody(nd)));
      loadList();
      reportDiscord(res.data.discord.state === 'skipped' ? null : res.data.discord, 'Published.');
    } catch (err) {
      toast.error(errText(err, 'The update could not be published.'));
    } finally {
      setBusy(false);
    }
  };

  const unpublish = () => setConfirm({
    title: 'Take this update down?',
    message: 'It disappears from the site and its Discord post is deleted. It stays here as a draft.',
    label: 'Take it down', danger: true,
    onConfirm: async () => {
      setConfirm(null);
      setBusy(true);
      try {
        const res = await api.post(`/changelog/admin/posts/${draft.id}/unpublish`);
        const nd = fromApi(res.data.post);
        setDraft(nd);
        setSaved(JSON.stringify(toBody(nd)));
        loadList();
        reportDiscord(res.data.discord?.state === 'none' ? null : res.data.discord, 'Taken down — it is a draft again.');
      } catch (err) {
        toast.error(errText(err, 'The update could not be taken down.'));
      } finally {
        setBusy(false);
      }
    },
  });

  const remove = () => setConfirm({
    title: 'Delete this update?',
    message: draft.status === 'published'
      ? 'It disappears from the site, its Discord post and its pictures are deleted. This cannot be undone.'
      : 'The draft and its pictures are deleted. This cannot be undone.',
    label: 'Delete', danger: true,
    onConfirm: async () => {
      setConfirm(null);
      if (!draft.id) { setDraft(null); setSaved(''); return; }
      setBusy(true);
      try {
        await api.delete(`/changelog/admin/posts/${draft.id}`);
        setDraft(null);
        setSaved('');
        loadList();
        toast.success('Deleted.');
      } catch (err) {
        toast.error(errText(err, 'The update could not be deleted.'));
      } finally {
        setBusy(false);
      }
    },
  });

  // ── képek ──
  const addImages = useCallback(async (files, si, replaceKey = null) => {
    const list2 = [...files].filter(f => f && f.type.startsWith('image/'));
    if (!list2.length) return;
    for (const file of list2) {
      const key = replaceKey || nextKey();
      setDraft(d => {
        const sections = d.sections.map(s => ({ ...s, blocks: [...s.blocks] }));
        const sec = sections[Math.min(si, sections.length - 1)];
        if (!sec) return d;
        const ph = { key, type: 'image', uploading: true, caption: '', imageId: null };
        const at = sec.blocks.findIndex(b => b.key === key);
        if (at >= 0) sec.blocks[at] = { ...sec.blocks[at], uploading: true };
        else sec.blocks.push(ph);
        return { ...d, sections };
      });
      try {
        const body = await prepareImage(file);
        if (body.size > MAX_IMAGE_BYTES) throw new Error('The picture is larger than 8 MB.');
        const res = await api.post('/changelog/admin/images', body, {
          headers: { 'Content-Type': body.type || 'application/octet-stream' }, timeout: 60000,
        });
        const img = res.data.image;
        setDraft(d => ({
          ...d,
          sections: d.sections.map(s => ({
            ...s,
            blocks: s.blocks.map(b => (b.key === key
              ? { ...b, uploading: false, imageId: img.id, url: img.url, width: img.width, height: img.height, bytes: img.bytes }
              : b)),
          })),
        }));
      } catch (err) {
        toast.error(errText(err, err.message || 'The picture could not be uploaded.'));
        setDraft(d => ({
          ...d,
          sections: d.sections.map(s => ({
            ...s,
            blocks: replaceKey
              ? s.blocks.map(b => (b.key === key ? { ...b, uploading: false } : b))
              : s.blocks.filter(b => b.key !== key),
          })),
        }));
      }
    }
  }, [toast]);

  // Ctrl+V: a vágólap képe az utoljára használt témába kerül.
  useEffect(() => {
    if (!draft || view !== 'edit') return undefined;
    const onPaste = (e) => {
      const files = [...(e.clipboardData?.files || [])].filter(f => f.type.startsWith('image/'));
      if (!files.length) return;
      e.preventDefault();
      addImages(files, activeSection.current);
    };
    const el = editorRef.current;
    el?.addEventListener('paste', onPaste);
    return () => el?.removeEventListener('paste', onPaste);
  }, [draft, view, addImages]);

  // ── import ──
  const applyImport = (parsed) => {
    update(d => {
      if (parsed.title && !d.title.trim()) d.title = parsed.title;
      if (parsed.summary && !d.summary.trim()) d.summary = parsed.summary;
      // A kiinduló üres témát a beimportált témák váltják le.
      const blank = d.sections.length === 1 && !d.sections[0].title && d.sections[0].blocks.every(b => b.type === 'item' && !b.text.trim());
      const add = parsed.sections.map(s => ({
        key: nextKey(), topic: s.topic, title: s.title,
        blocks: s.lines.map(l => ({ key: nextKey(), type: 'item', mark: l.mark, text: l.text })),
      }));
      d.sections = blank ? add : [...d.sections, ...add];
    });
    setImportOpen(false);
    toast.success(`Imported ${parsed.sections.length} topic${parsed.sections.length === 1 ? '' : 's'} — add the pictures.`);
  };

  return (
    <div className="flex flex-wrap gap-7 items-start">
      <aside className="flex-[1_1_240px] max-w-full lg:max-w-[290px] flex flex-col gap-2.5">
        <button type="button" className="btn-primary" onClick={newPost} disabled={busy}>
          <Icon className="w-4 h-4"><path d="M12 5v14" /><path d="M5 12h14" /></Icon>New update
        </button>
        <div className="card p-2 flex flex-col gap-0.5">
          {!list && <div className="skeleton h-14 m-1" />}
          {list && list.length === 0 && <p className="m-0 p-3 text-sm text-gray-400">No updates yet. Start with “New update”.</p>}
          {list && list.map(p => {
            const on = draft?.id === p.id;
            return (
              <button key={p.id} type="button" onClick={() => !on && openPost(p.id)}
                      className={`text-left flex flex-col gap-1 px-3.5 py-3 rounded-lg border transition-colors ${
                        on ? 'bg-gray-800 border-[#30343a]' : 'border-transparent hover:bg-[#181a1e]'}`}>
                <span className="flex items-center gap-2">
                  {p.status === 'draft'
                    ? <span className="font-mono text-[10.5px] tracking-[0.08em] text-warn border border-warn/45 rounded-[3px] px-1.5 py-px">DRAFT</span>
                    : <span className="font-mono text-[10.5px] tracking-[0.08em] text-ok border border-ok/40 rounded-[3px] px-1.5 py-px">LIVE</span>}
                  <span className="font-mono text-[11.5px] text-gray-400 truncate">
                    {p.status === 'draft'
                      ? `edited ${formatDate(p.updatedAt)} ${fmtTime(p.updatedAt)}${p.updatedBy ? ` by ${p.updatedBy}` : ''}`
                      : formatDate(p.publishedAt)}
                  </span>
                </span>
                <span className={`text-sm leading-[1.35] ${on ? 'text-white' : 'text-gray-300'}`}>{p.title}</span>
              </button>
            );
          })}
        </div>
      </aside>

      <div className="flex-[999_1_640px] min-w-0 flex flex-col gap-5" ref={editorRef}
           onDragOver={(e) => { if (draft && e.dataTransfer.types.includes('Files')) e.preventDefault(); }}
           onDrop={(e) => {
             if (draft && view === 'edit' && e.dataTransfer.files?.length) { e.preventDefault(); addImages(e.dataTransfer.files, activeSection.current); }
           }}>
        {!draft && (
          <div className="card px-6 py-10 text-center text-gray-400">
            Pick an update on the left, or start a new one.
            <span className="block mt-2 text-[13px] text-gray-500">
              It goes to <Link to="/changelog" className="link">the Changelog page</Link> and, if you want, to Discord.
            </span>
          </div>
        )}

        {draft && (
          <>
            <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2.5 border-b border-line">
              <nav aria-label="Editor views" className="flex gap-6">
                {[['edit', 'Edit'], ['preview', 'Preview'], ['discord', 'Discord']].map(([id, label]) => (
                  <button key={id} type="button" onClick={() => setView(id)} aria-current={view === id ? 'page' : undefined}
                          className={`inline-flex items-center h-10 -mb-px text-sm border-b-2 transition-colors ${
                            view === id ? 'border-brand-500 text-white font-semibold' : 'border-transparent text-gray-400 hover:text-white'}`}>
                    {label}
                  </button>
                ))}
              </nav>
              <div className="flex flex-wrap items-center gap-2 pb-2">
                <span className="font-mono text-xs text-gray-500">
                  {draft.status === 'published' ? 'Live' : 'Draft'}
                  {dirty ? ' · unsaved changes' : draft.updatedAt ? ` · saved ${fmtTime(draft.updatedAt)}` : ''}
                </span>
                <button type="button" className="btn-secondary h-8 px-3 py-0 text-[13px]" onClick={() => setImportOpen(true)}>
                  <Icon className="w-[15px] h-[15px]"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M10 9H8" /><path d="M16 13H8" /><path d="M16 17H8" /></Icon>
                  Import from text
                </button>
                <button type="button" className="btn-secondary h-8 px-3 py-0 text-[13px]" onClick={() => save()} disabled={busy || (!dirty && !!draft.id)}>
                  {draft.status === 'published' ? 'Save changes' : 'Save draft'}
                </button>
              </div>
            </div>

            {view === 'edit' && (
              <EditView draft={draft} update={update} addImages={addImages} activeSection={activeSection} />
            )}
            {view === 'preview' && (
              <article className="card p-6 sm:p-8 flex flex-col gap-[18px]">
                <PostHeader post={toView(draft)} />
                <TopicJump post={toView(draft)} idPrefix="pv" />
                <PostSections post={toView(draft)} idPrefix="pv" />
              </article>
            )}
            {view === 'discord' && <DiscordView draft={draft} ping={opts.ping} />}

            <PublishBar draft={draft} opts={opts} setOpts={setOpts} busy={busy || uploading} dirty={dirty}
                        discordEnabled={discordEnabled}
                        onPublish={publish} onSave={() => save()} onUnpublish={unpublish} onDelete={remove}
                        onDiscordView={() => setView('discord')} />
          </>
        )}
      </div>

      {confirm && (
        <ConfirmModal title={confirm.title} message={confirm.message} confirmLabel={confirm.label} danger={confirm.danger}
                      onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />
      )}
      {importOpen && <ImportModal onClose={() => setImportOpen(false)} onImport={applyImport} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// SZERKESZTÉS
// ════════════════════════════════════════════════════════════════════════════
function EditView({ draft, update, addImages, activeSection }) {
  const drag = useRef(null);

  const set = (field) => (e) => { const v = e.target.value; update(d => { d[field] = v; }); };
  const moveSection = (i, dir) => update(d => {
    const j = i + dir;
    if (j < 0 || j >= d.sections.length) return;
    [d.sections[i], d.sections[j]] = [d.sections[j], d.sections[i]];
  });
  const removeSection = (i) => update(d => { d.sections.splice(i, 1); });
  const addSection = () => update(d => {
    const used = new Set(d.sections.map(s => s.topic));
    const topic = (TOPICS.find(t => !used.has(t.id)) || TOPICS[0]).id;
    d.sections.push({ key: nextKey(), topic, title: '', blocks: [{ key: nextKey(), type: 'item', mark: 'new', text: '' }] });
    activeSection.current = d.sections.length - 1;
  });

  return (
    <>
      <section className="card p-5 grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
        <label className="block">
          <span className="block text-[13px] text-gray-400 mb-1.5">Version (optional)</span>
          <input className="input" value={draft.version} onChange={set('version')} placeholder="e.g. v2.5.0" maxLength={24} />
        </label>
        <div>
          <span className="block text-[13px] text-gray-400 mb-1.5">Date</span>
          <div className="input text-gray-500">
            {draft.publishedAt ? `Published ${formatDate(draft.publishedAt)}` : 'Set when you publish'}
          </div>
        </div>
        <label className="block col-span-full">
          <span className="block text-[13px] text-gray-400 mb-1.5">Title</span>
          <input className="input text-base font-semibold text-white" value={draft.title} onChange={set('title')} maxLength={160}
                 placeholder="What this update is about — e.g. Cloud routes — your routes on every PC" />
        </label>
        <label className="block col-span-full">
          <span className="block text-[13px] text-gray-400 mb-1.5">Summary — one to three sentences</span>
          <textarea className="textarea min-h-[5.5rem]" rows={3} value={draft.summary} onChange={set('summary')} maxLength={600} />
        </label>
        <label className="block col-span-full">
          <span className="block text-[13px] text-gray-400 mb-1.5">Action needed (optional — shown in amber)</span>
          <input className="input" value={draft.action} onChange={set('action')} maxLength={300}
                 placeholder="Only if the player has to do something — e.g. Restart the loader once." />
        </label>
      </section>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="panel-label">Topics</span>
        <span className="text-[13px] text-gray-500">
          In a line: <code className="code-inline">**bold**</code> for names on screen, <code className="code-inline">`code`</code> for button labels.
        </span>
      </div>

      {draft.sections.map((s, si) => (
        <TopicCard key={s.key} section={s} si={si} count={draft.sections.length} update={update} addImages={addImages}
                   drag={drag} onActive={() => { activeSection.current = si; }}
                   onMove={(dir) => moveSection(si, dir)} onRemove={() => removeSection(si)} />
      ))}

      <button type="button" className="btn-secondary self-start" onClick={addSection}>
        <Icon className="w-4 h-4"><path d="M12 5v14" /><path d="M5 12h14" /></Icon>Add topic
      </button>
    </>
  );
}

function TopicPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  // Felfelé nyílik, ha alatta nincs elég hely (pl. az import ablak alján — ott
  // a lista levágódott, mérve 2026-10-03).
  const [up, setUp] = useState(false);
  const [maxH, setMaxH] = useState(320);
  const ref = useRef(null);
  const toggle = () => {
    if (!open && ref.current) {
      // A VÁGÓ doboz a legközelebbi görgető szülő (a modal törzse), nem az ablak.
      let clip = { top: 0, bottom: window.innerHeight };
      for (let p = ref.current.parentElement; p && p !== document.body; p = p.parentElement) {
        if (/(auto|scroll|hidden)/.test(getComputedStyle(p).overflowY)) {
          const c = p.getBoundingClientRect();
          clip = { top: Math.max(0, c.top), bottom: Math.min(window.innerHeight, c.bottom) };
          break;
        }
      }
      const r = ref.current.getBoundingClientRect();
      const below = clip.bottom - r.bottom - 8;
      const above = r.top - clip.top - 8;
      const goUp = below < 320 && above > below;
      setUp(goUp);
      setMaxH(Math.max(160, Math.min(320, goUp ? above : below)));
    }
    setOpen(o => !o);
  };
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const key = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', key); };
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button type="button" aria-haspopup="listbox" aria-expanded={open} aria-label={`Topic: ${TOPIC_LABEL[value]}`}
              onClick={toggle}
              className="inline-flex items-center gap-2 h-10 px-2.5 rounded-lg border border-line-strong bg-gray-950 hover:border-line-hover">
        <span className="text-brand-500 flex"><TopicIcon topic={value} /></span>
        <span className="text-white text-sm">{TOPIC_LABEL[value]}</span>
        <Icon className="w-3.5 h-3.5 text-gray-500"><path d="m6 9 6 6 6-6" /></Icon>
      </button>
      {open && (
        <ul role="listbox" style={{ maxHeight: maxH }}
            className={`absolute z-20 ${up ? 'bottom-full mb-1' : 'mt-1'} w-72 overflow-y-auto list-none m-0 p-1
                        rounded-xl border border-[#30343a] bg-gray-900 shadow-card-lg`}>
          {TOPICS.map(t => (
            <li key={t.id}>
              <button type="button" role="option" aria-selected={t.id === value}
                      onClick={() => { onChange(t.id); setOpen(false); }}
                      className={`w-full text-left flex items-start gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-800 ${t.id === value ? 'bg-gray-800' : ''}`}>
                <span className="text-brand-500 pt-0.5 flex"><TopicIcon topic={t.id} /></span>
                <span className="min-w-0">
                  <span className="block text-sm text-white">{t.label}</span>
                  <span className="block text-xs text-gray-500">{t.hint}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function IconBtn({ label, onClick, disabled, children }) {
  return (
    <button type="button" className="btn-icon w-8 h-8" aria-label={label} title={label} onClick={onClick} disabled={disabled}>
      <Icon className="w-4 h-4">{children}</Icon>
    </button>
  );
}
const UP = <path d="m18 15-6-6-6 6" />;
const DOWN = <path d="m6 9 6 6 6-6" />;
const TRASH = <><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>;

function MarkSwitch({ value, onChange }) {
  const on = {
    new: 'bg-ok/[0.12] text-ok', improved: 'bg-brand-500/[0.14] text-brand-400',
    fixed: 'bg-line text-gray-100', removed: 'bg-danger/[0.12] text-danger',
  };
  return (
    <div role="group" aria-label="Kind" className="inline-flex flex-shrink-0 rounded-lg border border-line-strong overflow-hidden">
      {MARKS.map(m => (
        <button key={m.id} type="button" aria-pressed={value === m.id} onClick={() => onChange(m.id)}
                className={`px-[9px] py-1 font-mono text-xs uppercase tracking-[0.06em] transition-colors ${
                  value === m.id ? on[m.id] : 'text-gray-500 hover:text-gray-200'}`}>
          {m.label}
        </button>
      ))}
    </div>
  );
}

function TopicCard({ section, si, count, update, addImages, drag, onActive, onMove, onRemove }) {
  const fileRef = useRef(null);
  const replaceRef = useRef(null);
  const [over, setOver] = useState(false);

  const setBlock = (bi, patch) => update(d => { d.sections[si].blocks[bi] = { ...d.sections[si].blocks[bi], ...patch }; });
  const moveBlock = (bi, dir) => update(d => {
    const b = d.sections[si].blocks;
    const j = bi + dir;
    if (j < 0 || j >= b.length) return;
    [b[bi], b[j]] = [b[j], b[bi]];
  });
  const removeBlock = (bi) => update(d => { d.sections[si].blocks.splice(bi, 1); });
  const addLine = () => update(d => { d.sections[si].blocks.push({ key: nextKey(), type: 'item', mark: 'new', text: '' }); });

  // Húzás: sor / kép a témán belül.
  const onDragStart = (bi) => (e) => {
    drag.current = { si, bi };
    e.dataTransfer.effectAllowed = 'move';
    // A húzott kép az egész sor legyen, ne csak a fogantyú.
    const row = e.currentTarget.closest('[data-row]');
    if (row) e.dataTransfer.setDragImage(row, 16, 16);
  };
  const onDropRow = (bi) => (e) => {
    const from = drag.current;
    if (e.dataTransfer.files?.length) return;   // a fájl a dobó-mezőé
    e.preventDefault();
    drag.current = null;
    if (!from || from.si !== si || from.bi === bi) return;
    update(d => {
      const b = d.sections[si].blocks;
      const [item] = b.splice(from.bi, 1);
      b.splice(bi, 0, item);
    });
  };

  return (
    <section className="card px-[18px] py-4 flex flex-col gap-2" onFocusCapture={onActive} onMouseDownCapture={onActive}
             onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setOver(true); } }}
             onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(false); }}
             onDrop={(e) => { if (e.dataTransfer.files?.length) { e.preventDefault(); setOver(false); addImages(e.dataTransfer.files, si); } }}>
      <div className="flex flex-wrap items-center gap-2.5">
        <TopicPicker value={section.topic} onChange={(t) => update(d => { d.sections[si].topic = t; })} />
        <input className="input flex-[1_1_280px] min-w-0 font-semibold text-white" value={section.title} maxLength={120}
               aria-label="Topic title" placeholder="Own title — e.g. Melt — split into pages"
               onChange={(e) => { const v = e.target.value; update(d => { d.sections[si].title = v; }); }} />
        <span className="flex gap-0.5">
          <IconBtn label="Move topic up" onClick={() => onMove(-1)} disabled={si === 0}>{UP}</IconBtn>
          <IconBtn label="Move topic down" onClick={() => onMove(1)} disabled={si === count - 1}>{DOWN}</IconBtn>
          <IconBtn label="Delete topic" onClick={onRemove}>{TRASH}</IconBtn>
        </span>
      </div>

      <div className="flex flex-col">
        {section.blocks.map((b, bi) => (
          <div key={b.key} data-row={b.key}
               onDragOver={(e) => { if (!e.dataTransfer.types.includes('Files')) e.preventDefault(); }}
               onDrop={onDropRow(bi)}
               className="flex flex-wrap items-start gap-x-2.5 gap-y-2 py-2.5 border-t border-gray-800">
            <span draggable={!b.uploading} onDragStart={onDragStart(bi)}
                  className="text-[#5a5f68] hover:text-gray-300 pt-1.5 flex cursor-grab active:cursor-grabbing" aria-hidden="true" title="Drag to move">
              <Icon className="w-4 h-4"><circle cx="9" cy="6" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="18" r="1" /><circle cx="15" cy="6" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="18" r="1" /></Icon>
            </span>
            {b.type === 'item' ? (
              <>
                <MarkSwitch value={b.mark} onChange={(m) => setBlock(bi, { mark: m })} />
                <textarea className="input flex-[1_1_360px] min-w-0 resize-y text-[13.5px] leading-[1.5] py-2" rows={2}
                          aria-label="Line text" maxLength={1000} value={b.text} placeholder="What changed — one sentence."
                          onChange={(e) => setBlock(bi, { text: e.target.value })} />
              </>
            ) : (
              <div className="flex flex-wrap items-start gap-x-3.5 gap-y-2.5 flex-[1_1_420px] min-w-0">
                {b.uploading
                  ? <div className="w-[180px] h-[110px] rounded-lg border border-[#30343a] grid place-items-center text-xs text-gray-400 skeleton">Uploading…</div>
                  : <img src={b.url} alt={b.caption || ''} className="w-[180px] h-auto rounded-lg border border-[#30343a] block flex-shrink-0" />}
                <div className="flex-[1_1_240px] min-w-0 flex flex-col gap-2">
                  <label className="block">
                    <span className="block text-[13px] text-gray-400 mb-1.5">Caption</span>
                    <input className="input" value={b.caption} maxLength={200} placeholder="What to look at in the picture"
                           onChange={(e) => setBlock(bi, { caption: e.target.value })} />
                  </label>
                  {!b.uploading && (
                    <span className="font-mono text-[11.5px] text-gray-500">
                      {b.width} × {b.height}{b.bytes ? ` · ${fmtBytes(b.bytes)}` : ''}
                    </span>
                  )}
                  <span>
                    <button type="button" className="btn-secondary h-8 px-3 py-0 text-[13px]" disabled={b.uploading}
                            onClick={() => { replaceRef.current = b.key; fileRef.current?.click(); }}>
                      Replace
                    </button>
                  </span>
                </div>
              </div>
            )}
            <span className="flex gap-0.5 ml-auto">
              <IconBtn label="Move up" onClick={() => moveBlock(bi, -1)} disabled={bi === 0}>{UP}</IconBtn>
              <IconBtn label="Move down" onClick={() => moveBlock(bi, 1)} disabled={bi === section.blocks.length - 1}>{DOWN}</IconBtn>
              <IconBtn label={b.type === 'image' ? 'Remove picture' : 'Delete line'} onClick={() => removeBlock(bi)}>{TRASH}</IconBtn>
            </span>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1.5">
        <button type="button" className="btn-secondary h-8 px-3 py-0 text-[13px]" onClick={addLine}>
          <Icon className="w-[15px] h-[15px]"><path d="M12 5v14" /><path d="M5 12h14" /></Icon>Line
        </button>
        <button type="button" className="btn-secondary h-8 px-3 py-0 text-[13px]"
                onClick={() => { replaceRef.current = null; fileRef.current?.click(); }}>
          <Icon className="w-[15px] h-[15px]"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.09-3.09a2 2 0 0 0-2.82 0L6 21" /></Icon>Image
        </button>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple={false} hidden
               onChange={(e) => {
                 const files = e.target.files;
                 if (files?.length) addImages(files, si, replaceRef.current);
                 replaceRef.current = null;
                 e.target.value = '';
               }} />
      </div>

      <div className={`rounded-xl border border-dashed p-4 flex flex-wrap items-center justify-center gap-2.5 text-[13.5px] text-center transition-colors ${
             over ? 'border-brand-500 bg-brand-500/[0.06] text-gray-100' : 'border-line-strong text-gray-400'}`}>
        <Icon className="w-[18px] h-[18px]"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.09-3.09a2 2 0 0 0-2.82 0L6 21" /></Icon>
        Drop a screenshot here, or paste it with <span className="font-mono text-gray-100">Ctrl+V</span> — PNG, JPG, WebP or GIF, up to 8 MB
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// KÖZZÉTÉTEL
// ════════════════════════════════════════════════════════════════════════════
function PublishBar({ draft, opts, setOpts, busy, dirty, discordEnabled, onPublish, onSave, onUnpublish, onDelete, onDiscordView }) {
  if (draft.status === 'published') {
    return (
      <div className="card px-5 py-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-line-strong">
        <span className="text-sm text-gray-300">
          Live since {formatDate(draft.publishedAt)}
          {draft.discordMessages ? ` · on Discord (${draft.discordMessages} message${draft.discordMessages === 1 ? '' : 's'})` : ' · not on Discord'}
          {' · '}<Link to={`/changelog/${draft.slug}`} className="link">open it</Link>
        </span>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-danger" onClick={onDelete} disabled={busy}>Delete</button>
          <button type="button" className="btn-secondary" onClick={onUnpublish} disabled={busy}>Take it down</button>
          <button type="button" className="btn-primary" onClick={onSave} disabled={busy || !dirty}>Save changes</button>
        </div>
      </div>
    );
  }
  // A szerver nem posztol a Discordra (béta / helyi gép, lásd a backend
  // utils/discord.js `changelogDiscordEnabled`) — előre kimondjuk, nem a közzététel után.
  if (!discordEnabled) {
    return (
      <div className="card px-5 py-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-line-strong">
        <span className="text-sm text-gray-400">
          Discord posting is off on this server — publishing puts the update on the site only.
        </span>
        <div className="flex flex-wrap gap-2">
          {draft.id && <button type="button" className="btn-danger" onClick={onDelete} disabled={busy}>Delete</button>}
          <button type="button" className="btn-secondary" onClick={onDiscordView}>See the Discord post</button>
          <button type="button" className="btn-primary" onClick={onPublish} disabled={busy}>Publish</button>
        </div>
      </div>
    );
  }
  return (
    <div className="card px-5 py-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-line-strong">
      <div className="flex flex-wrap gap-x-6 gap-y-2.5">
        <label className="flex items-center gap-2.5 text-sm text-gray-100 cursor-pointer">
          <input type="checkbox" className="checkbox accent-brand-500" checked={opts.discord} onChange={(e) => setOpts(o => ({ ...o, discord: e.target.checked }))} />
          Post to Discord
        </label>
        <label className={`flex items-center gap-2.5 text-sm cursor-pointer ${opts.discord ? 'text-gray-100' : 'text-gray-500'}`}>
          <input type="checkbox" className="checkbox accent-brand-500" checked={opts.ping} disabled={!opts.discord}
                 onChange={(e) => setOpts(o => ({ ...o, ping: e.target.checked }))} />
          Ping @everyone
        </label>
      </div>
      <div className="flex flex-wrap gap-2">
        {draft.id && <button type="button" className="btn-danger" onClick={onDelete} disabled={busy}>Delete</button>}
        <button type="button" className="btn-secondary" onClick={onDiscordView}>See the Discord post</button>
        <button type="button" className="btn-primary" onClick={onPublish} disabled={busy}>Publish</button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// DISCORD-ELŐNÉZET — ugyanaz a darabolás, mint a botban (dcbot changelogEvent.js)
// ════════════════════════════════════════════════════════════════════════════
const MARK_EMOJI = { new: '🆕', improved: '✨', fixed: '🔧', removed: '🗑️' };
const TOPIC_EMOJI = { autopilot: '🧭', farm: '📦', quests: '📜', visuals: '👁️', tools: '🛠️', settings: '⚙️', loader: '⬇️', website: '🌐', other: '📌' };

function discordMessages(post) {
  const embeds = [];
  let head = post.summary || '';
  if (post.action) head += `${head ? '\n\n' : ''}**⚠️ Action needed:** ${post.action}`;
  embeds.push({ title: `🆙 Update${post.version ? ` ${post.version}` : ''} — ${post.title}`, lines: null, text: head, image: null });
  for (const s of post.sections) {
    const title = `${TOPIC_EMOJI[s.topic]} ${TOPIC_LABEL[s.topic]}${s.title ? ` — ${s.title}` : ''}`;
    const lines = s.blocks.filter(b => b.type === 'item');
    const images = s.blocks.filter(b => b.type === 'image' && b.url);
    // A 4096-os leírás-korlát: sorhatáron tovább.
    const chunks = [];
    let cur = [], len = 0;
    for (const l of lines) {
      const n = l.text.length + 4;
      if (cur.length && len + n > 4096) { chunks.push(cur); cur = []; len = 0; }
      cur.push(l); len += n;
    }
    chunks.push(cur);
    chunks.forEach((c, i) => embeds.push({ title: i === 0 ? title : `${title} (cont.)`, lines: c, text: '', image: null }));
    if (images.length) {
      embeds[embeds.length - 1].image = images[0];
      images.slice(1).forEach(img => embeds.push({ title: '', lines: null, text: '', image: img }));
    }
  }
  const size = e => e.title.length + e.text.length + (e.lines || []).reduce((a, l) => a + l.text.length + 4, 0) + (e.image?.caption?.length || 0);
  const out = [];
  let cur = [], chars = 0;
  for (const e of embeds) {
    const n = size(e);
    if (cur.length && (cur.length >= 10 || chars + n > 6000)) { out.push(cur); cur = []; chars = 0; }
    cur.push(e); chars += n;
  }
  if (cur.length) out.push(cur);
  return out;
}

function DiscordView({ draft, ping }) {
  const post = toView(draft);
  const msgs = discordMessages(post);
  const pics = post.sections.reduce((a, s) => a + s.blocks.filter(b => b.type === 'image' && b.url).length, 0);
  const embedsN = msgs.reduce((a, m) => a + m.length, 0);
  const url = `${window.location.origin}/changelog/${draft.slug || 'your-update'}`;
  return (
    <div className="flex flex-col gap-3.5">
      <p className="m-0 text-sm text-gray-400">
        What goes to the Discord update channel when you publish:{' '}
        <b className="text-gray-100 font-semibold">
          {msgs.length} message{msgs.length === 1 ? '' : 's'} · {embedsN} embed{embedsN === 1 ? '' : 's'} · {pics} picture{pics === 1 ? '' : 's'}
        </b>. Discord fits 10 embeds and 6000 characters in one message, so a long update is split by topic.
      </p>
      {msgs.map((m, mi) => (
        <div key={mi} className="card px-[18px] py-4">
          <span className="panel-label block mb-3">Message {mi + 1} of {msgs.length}</span>
          <div className="flex items-center gap-2.5 mb-2">
            <span className="w-9 h-9 rounded-full bg-gray-800 border border-line-strong grid place-items-center font-mono text-xs text-brand-500">ME</span>
            <span className="font-semibold text-white">MortisEngine</span>
            <span className="font-mono text-[10.5px] text-gray-950 bg-brand-500 rounded-[3px] px-[5px]">BOT</span>
          </div>
          {mi === 0 && ping && <div className="text-sm text-brand-400 mb-2">@everyone</div>}
          <div className="flex flex-col gap-2">
            {m.map((e, ei) => (
              <div key={ei} className="flex rounded-lg overflow-hidden bg-gray-800 max-w-[560px]">
                <span className="w-1 flex-shrink-0 bg-brand-500" />
                <div className="px-3.5 py-2.5 text-[13.5px] min-w-0">
                  {e.title && <div className="font-semibold text-white mb-1.5">{e.title}</div>}
                  {e.text && <p className="m-0 mb-1 text-[#c9ccd1] leading-normal whitespace-pre-line"><Rich text={e.text} /></p>}
                  {e.lines && (
                    <ul className="list-none m-0 p-0 text-[#c9ccd1]">
                      {e.lines.map((l, li) => <li key={li} className="mb-1.5 leading-normal">{MARK_EMOJI[l.mark]} <Rich text={l.text} /></li>)}
                    </ul>
                  )}
                  {e.image && (
                    <>
                      <img src={e.image.url} alt="" className="block max-w-full mt-2.5 rounded-lg" style={{ width: e.lines ? 360 : 220 }} />
                      {e.image.caption && <div className="mt-1.5 text-xs text-gray-400">{e.image.caption}</div>}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
          {mi === msgs.length - 1 && (
            <p className="m-0 mt-2.5 text-[13.5px] text-[#c9ccd1]">📖 Full notes with pictures: <span className="text-brand-400">{url}</span></p>
          )}
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// IMPORT A DISCORD-SZÖVEGBŐL
// ════════════════════════════════════════════════════════════════════════════
// A téma a fejlécből: a téma neve (pl. „(Visuals)”, „Loader”), vagy kulcsszó.
const KEYWORDS = [
  ['loader', ['loader', 'auto restart', 'restart', 'sync']],
  ['website', ['website', 'web site', 'dashboard', 'site']],
  ['quests', ['quest', 'challenge', 'fortune']],
  ['visuals', ['visual', 'name', 'camera', 'esp']],
  ['farm', ['farm', 'loot', 'bag', 'melt', 'bloodshed', 'gather', 'stats', 'sell', 'item filter', 'cube']],
  ['autopilot', ['autopilot', 'route', 'play', 'editor', 'combat', 'party', 'revive', 'key farm', 'pathfinding', 'jump', 'ready-made', 'run']],
  ['tools', ['tool']],
  ['settings', ['setting']],
];

function stripEmoji(s) {
  return s.replace(/[\p{Extended_Pictographic}\u{1F1E6}-\u{1F1FF}️‍]/gu, '').replace(/\s+/g, ' ').trim();
}

export function parseImport(text) {
  const out = { title: '', summary: '', sections: [], unmarked: 0 };
  let cur = null;
  let inSummary = false;
  const summary = [];
  for (const raw of String(text || '').split(/\r?\n/)) {
    const line = raw.replace(/\s+$/, '');
    if (/^#\s+/.test(line) && !/^##/.test(line)) {
      const t = stripEmoji(line.replace(/^#\s+/, ''));
      if (!/^mortisengine\s*[—–-]\s*update$/i.test(t)) out.title = t;
      inSummary = true;
      continue;
    }
    if (/^##+\s+/.test(line)) {
      inSummary = false;
      const head = stripEmoji(line.replace(/^##+\s+/, ''));
      const paren = head.match(/\(([^)]+)\)\s*$/);
      const lower = head.toLowerCase();
      let topic = null, how = '';
      const byName = TOPICS.find(t => (paren && paren[1].toLowerCase() === t.label.toLowerCase()) || new RegExp(`(^|\\W)${t.label.toLowerCase()}(\\W|$)`).test(lower));
      if (byName) { topic = byName.id; how = `from “${byName.label}”`; }
      else {
        for (const [id, words] of KEYWORDS) {
          const w = words.find(k => lower.includes(k));
          if (w) { topic = id; how = `guessed from “${w}”`; break; }
        }
      }
      // A saját cím: a „(Visuals)” zárójel és az elöl álló „Loader — ” / „Autopilot: ”
      // nélkül — a téma neve úgyis ott áll a téma fölött.
      let title = (paren && TOPICS.some(t => t.label.toLowerCase() === paren[1].toLowerCase()))
        ? head.replace(/\s*\([^)]+\)\s*$/, '') : head;
      if (byName) {
        const pre = title.match(new RegExp(`^${byName.label}\\s*[:—–-]\\s*`, 'i'));
        if (pre) title = title.slice(pre[0].length);
      }
      cur = { source: line.trim(), topic: topic || 'other', how: topic ? how : 'no topic word — check it', unsure: !topic, title, lines: [] };
      out.sections.push(cur);
      continue;
    }
    if (!line.trim()) { if (inSummary && summary.length) inSummary = false; continue; }
    if (inSummary && !cur) { summary.push(line.trim()); continue; }
    if (!cur) continue;
    // Egy sor: "- ", "• ", "* " (behúzva is), vagy egy sima bekezdés-sor.
    let t = line.trim().replace(/^[-•*]\s+/, '');
    t = t.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1$2');   // *dőlt* -> sima (nincs dőlt)
    let mark = null;
    const m = t.match(/^(new|improved|fixed|removed)\b[:\s—-]*\s*/i);
    if (m) { mark = m[1].toLowerCase(); t = t.slice(m[0].length); }
    if (!t) continue;
    if (!mark) { mark = 'new'; out.unmarked++; }
    cur.lines.push({ mark, text: t.slice(0, 1000) });
  }
  out.summary = summary.join(' ').slice(0, 600);
  out.sections = out.sections.filter(s => s.lines.length);
  return out;
}

function ImportModal({ onClose, onImport }) {
  const [text, setText] = useState('');
  const parsed = useMemo(() => parseImport(text), [text]);
  const [topics, setTopics] = useState({});
  const lines = parsed.sections.reduce((a, s) => a + s.lines.length, 0);
  const final = { ...parsed, sections: parsed.sections.map((s, i) => ({ ...s, topic: topics[i] || s.topic })) };
  return (
    <Modal size="5xl" title="Import from text" onClose={onClose}
           subtitle="Paste an update written in the Discord format. It becomes topics and lines — then add the pictures."
           footer={(
             <div className="flex justify-end gap-2">
               <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
               <button type="button" className="btn-primary" disabled={!parsed.sections.length} onClick={() => onImport(final)}>
                 Import {parsed.sections.length} topic{parsed.sections.length === 1 ? '' : 's'}
               </button>
             </div>
           )}>
      <div className="flex flex-wrap gap-[22px]">
        <label className="block flex-[1_1_420px] min-w-0">
          <span className="block text-[13px] text-gray-400 mb-1.5">Text</span>
          <textarea className="textarea font-mono text-[12.5px] leading-[1.55] min-h-[22rem]" value={text}
                    onChange={(e) => { setText(e.target.value); setTopics({}); }}
                    placeholder={'# Title\nSummary in one paragraph.\n\n## Farm: Melt — split into pages\n- NEW Split into separate pages under Farm.\n- FIXED Cubes: Per burst is no longer hidden.'} />
          <span className="block mt-2 text-[12.5px] text-gray-500">
            <span className="font-mono text-gray-200"># </span>title · first paragraph = summary ·{' '}
            <span className="font-mono text-gray-200">## </span>a topic · <span className="font-mono text-gray-200">- </span>a line ·{' '}
            <span className="font-mono text-gray-200">NEW / IMPROVED / FIXED / REMOVED</span> after the dash sets the mark
          </span>
        </label>
        <div className="flex-[1_1_420px] min-w-0 flex flex-col gap-2.5">
          <span className="panel-label">What it found</span>
          {!parsed.sections.length && <p className="m-0 text-sm text-gray-500">Nothing yet — paste the text on the left.</p>}
          {parsed.sections.length > 0 && (
            <div className="text-sm text-gray-100">
              {parsed.title && <b className="font-semibold">{parsed.title} · </b>}
              {parsed.sections.length} topic{parsed.sections.length === 1 ? '' : 's'} · {lines} line{lines === 1 ? '' : 's'}
            </div>
          )}
          <ul className="list-none m-0 p-0">
            {parsed.sections.map((s, i) => (
              <li key={i} className="py-3 border-t border-line">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <span className="font-mono text-xs text-gray-400 flex-[1_1_160px] min-w-0 truncate">{s.source}</span>
                  <span className="text-[#5a5f68]">→</span>
                  <TopicPicker value={topics[i] || s.topic} onChange={(t) => setTopics(x => ({ ...x, [i]: t }))} />
                  <span className={`font-mono text-[11px] ${s.unsure && !topics[i] ? 'text-warn' : 'text-gray-500'}`}>{topics[i] ? 'set by you' : s.how}</span>
                  <span className="font-mono text-xs text-gray-500">{s.lines.length} line{s.lines.length === 1 ? '' : 's'}</span>
                </div>
              </li>
            ))}
          </ul>
          {parsed.unmarked > 0 && (
            <div className="flex gap-3 items-start rounded-xl border border-warn/45 bg-warn/[0.08] px-4 py-3 text-[13.5px] text-gray-50">
              <span className="text-warn"><Icon className="w-[17px] h-[17px]"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" /><path d="M12 9v4" /><path d="M12 17h.01" /></Icon></span>
              <div>{parsed.unmarked} line{parsed.unmarked === 1 ? ' has' : 's have'} no mark — {parsed.unmarked === 1 ? 'it comes' : 'they come'} in as <b className="font-semibold">NEW</b>. Change them in the editor if needed.</div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
